<template>
  <div v-if="canChangeHandler" class="handler-control">
    <!-- An AppSelect without a label points aria-labelledby at its own id, so the title shares that id. -->
    <span id="application-handler">Handläggare:</span>
    <div class="handler-select">
      <AppSelect
        id="application-handler"
        v-model="selectedUserId"
        :options="personnelOptions"
        :disabled="isSaving"
        placeholder="Ingen vald"
        hide-details
        slim
      />
    </div>
    <AppButton type="button" color="green" small :disabled="!canSave" :loading="isSaving" @click="saveHandler">
      Spara
    </AppButton>
  </div>
  <span v-else>Handläggare: {{ getPersonnelNameByUserId(currentUserId) || '-' }}</span>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import { AppButton, AppSelect, useNotifications } from '@staff/ui'

import { setBrokerPersonnel, type ApplicationStatus } from '@/services/application'
import { usePersonnelDirectory } from '@/composables/usePersonnelDirectory'
import { useApplicationRecord } from './application.context'

// Drafts change handler in the application form; closed cases keep the one they had.
const HANDLER_CHANGEABLE_STATUSES: ApplicationStatus[] = ['submitted', 'working', 'approved', 'complete', 'signed']

const { application } = useApplicationRecord()
const refreshApplication = inject<() => Promise<void>>('refreshApplication')
const { notify } = useNotifications()
const { personnelOptions, getPersonnelNameByUserId } = usePersonnelDirectory()

const selectedUserId = ref('')
const isSaving = ref(false)

const currentUserId = computed(() => application.value?.brokerPersonnelUserId ?? '')
const canChangeHandler = computed(() =>
  Boolean(application.value && HANDLER_CHANGEABLE_STATUSES.includes(application.value.status)),
)
const canSave = computed(
  () => Boolean(selectedUserId.value) && selectedUserId.value !== currentUserId.value && !isSaving.value,
)

watch(
  currentUserId,
  (value) => {
    selectedUserId.value = value
  },
  { immediate: true },
)

const saveHandler = async () => {
  if (!application.value?.id || !canSave.value) return

  try {
    isSaving.value = true
    await setBrokerPersonnel(application.value.id, selectedUserId.value)
    await refreshApplication?.()
    notify({ type: 'success', title: 'Handläggaren har bytts' })
  } catch (err) {
    notify({
      type: 'error',
      title: err instanceof Error ? err.message : 'Kunde inte byta handläggare',
      dismissable: true,
    })
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped lang="scss">
.handler-control {
  display: flex;
  align-items: center;
  gap: var(--space-8);
}

// Fits a full name; sized in rem so it grows with the text.
.handler-select {
  width: 12rem;
}
</style>
